'use client';
import cn from 'classnames';
import Image from 'next/image';

import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';

import './BuilderSiteModal.style.scss';
import { FC, useState, useEffect } from 'react';
import BaseModal from '@/components/modal/lib/BaseModal';

export interface BuilderSiteModalCMSContent {
  /** Modal name */
  modalName: string;
  /** Text content to display in the modal on desktop */
  modalTextDesktop: string;

  /** Text content to display in the modal on mobile */
  modalTextMobile: string;

  /** Header content to display in the modal on desktop */
  modalHeaderDesktop: string;

  /** Header content to display in the modal on mobile */
  modalHeaderMobile: string;

  /** Button text for the call-to-action on desktop */
  buttonText?: string;

  /** Target for the button to link to */
  buttonTarget?: string;

  /** Button color */
  buttonColor?: string;

  /** Whether to open the link in a new tab */
  openInNewTab?: boolean;

  /** Background color for the modal */
  backgroundColor?: string;

  /** Text color for the modal */
  textColor?: string;

  /** How many seconds to wait before popping up the modal */
  initialDelay: number;

  /** Image to display in the modal on desktop */
  modalImageDesktop?: string;

  /** Image to display in the modal on mobile */
  modalImageMobile?: string;

  /** Whether to show the modal in the app */
  showInApp?: boolean;

  /** Optional CSS class name */
  className?: string;

  enabled: boolean;
}

export interface BuilderSiteModalProps {
  cmsContent?: BuilderSiteModalCMSContent;

  /** Function to call when the modal is dismissed */
  handleDismiss?: () => void;
}

const BuilderSiteModal: FC<BuilderSiteModalProps> = ({
  cmsContent,
  handleDismiss
}) => {
  const {
    modalTextDesktop,
    modalTextMobile,
    modalHeaderDesktop,
    modalHeaderMobile,
    buttonText,
    buttonTarget = '',
    buttonColor = '#121212',
    openInNewTab = false,
    textColor = '#121212', // Default dark text
    className = '',
    modalImageDesktop,
    modalImageMobile,
    enabled
  } = cmsContent || {};

  const [isMobile, setIsMobile] = useState(false);

  // Use effect to see whether window is mobile or desktop style in order to render different components and text
  useEffect(() => {
    // Check if window is available (client-side)
    if (typeof window !== 'undefined') {
      // Set initial state
      setIsMobile(window.innerWidth < 768);

      // Define the resize handler
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };

      // Add event listener
      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Check to see if the component is disabled
  if (!enabled || !handleDismiss) {
    return null;
  }

  // If we have no text content for the current screen size, render nothing. Should only happen when no builder is published
  if (
    isMobile
      ? !modalTextMobile && !modalHeaderMobile && !buttonText
      : !modalTextDesktop && !modalHeaderDesktop && !buttonText
  ) {
    return null;
  }

  const modalText = isMobile ? modalTextMobile : modalTextDesktop;
  const modalHeader = isMobile ? modalHeaderMobile : modalHeaderDesktop;
  const modalImage = isMobile ? modalImageMobile : modalImageDesktop;

  return (
    <BaseModal
      className={cn('BuilderSiteModal', className)}
      onClose={handleDismiss}
    >
      <div className='BuilderSiteModal-content'>
        <div className='BuilderSiteModal-text-and-button-container'>
          <div className='BuilderSiteModal-header-container'>
            <Text
              variant='title-l'
              style={{ color: textColor }}
              className={cn('BuilderSiteModal-header')}
            >
              {modalHeader}
            </Text>
          </div>
          <div className='BuilderSiteModal-text-container'>
            <Text
              variant='text-m'
              style={{ color: textColor }}
              className={cn('BuilderSiteModal-text')}
            >
              {modalText}
            </Text>
          </div>
          <div className='BuilderSiteModal-button-container'>
            <Button
              variant='light'
              className='Button small BuilderSiteModal-button'
              onClick={(e) => {
                e.stopPropagation();
                if (openInNewTab) {
                  handleDismiss();
                  window.open(buttonTarget, '_blank');
                } else {
                  handleDismiss();
                  window.open(buttonTarget, '_self');
                }
              }}
              style={{
                borderColor: buttonColor,
                color: buttonColor
              }}
            >
              <Text
                style={{ color: buttonColor }}
                variant='title-xxs-500-upper'
              >
                {buttonText}
              </Text>
            </Button>
          </div>
        </div>
        <div className='BuilderSiteModal-image-container'>
          {modalImage && (
            <Image
              src={modalImage}
              width={390}
              height={310}
              alt='Modal image'
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
};

export default BuilderSiteModal;
